// src/Pages/FollowUpPage_v1.js
import React, { useEffect, useState } from "react";
import axios from "axios";

// MUI Components
import { Container, Paper, Box, Grid, Typography, Card, CardContent, Button, IconButton, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme, } from "@mui/material";

// React Icons
import { MdExpandMore } from "react-icons/md";
import { HiOutlineClock, HiOutlineCalendar, HiOutlinePlusCircle, HiOutlineXCircle } from "react-icons/hi";
import { FaHourglassHalf } from "react-icons/fa6";
import { FaCalendarAlt } from "react-icons/fa";

// MUI History icon
import HistoryIcon from "@mui/icons-material/History";
import FormattedDate from "../components/FormattedDate";
import DateInput from "../components/DateInput";
import FloatingLabelInput from "../components/FloatingLabelInput";
import Dropdown from "../components/Dropdown";
import EnquiryForm from "./InquiryForm";
import HistoryManagement from "./HistoryManagementv1";

// Base API path and category list for enquiries.
// const path = "http://103.251.218.254:3100";

const categories = ["Missed", "Today", "Upcomming", "Delayed", "Failed"];

// Helper function to split records for a team leader.
const splitRecordsForTeamLeader = (records, userId) => {
    const selfRecords = records.filter(record => record.userId === userId);
    const teamRecords = records.filter(record => record.userId !== userId);
    return { self: selfRecords, team: teamRecords };
};

const FollowUpPage_v1 = ({ loggedInUser, setLoggedInUser, connectionString }) => {
    
    const path = connectionString;

    // -------------------- STATE DECLARATIONS --------------------
    // For regular users: dataSets[category] is an array.
    // For team leaders: dataSets[category] is an object { self: [], team: [] }.
    const [dataSets, setDataSets] = useState({});
    const [visibleTable, setVisibleTable] = useState(null);
    const [recordCounts, setRecordCounts] = useState({});
    const [offsets, setOffsets] = useState({});
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [inquiryNumber, setInquiryNumber] = useState("");

    // Pagination state.
    const [totalRecords, setTotalRecords] = useState(0);
    const [displayedRecords, setDisplayedRecords] = useState(0);

    // Individual search field state variables.
    const [searchInquiryDate, setSearchInquiryDate] = useState("");
    const [searchInquiryNumber, setSearchInquiryNumber] = useState("");
    const [searchStatus, setSearchStatus] = useState("");
    const [searchAssign, setSearchAssign] = useState("");
    const [searchCompanyName, setSearchCompanyName] = useState("");
    const [searchContactPerson, setSearchContactPerson] = useState("");
    const [searchMobileNo, setSearchMobileNo] = useState("");

    // Dropdown options for filtering.
    const [statusOptions, setStatusOptions] = useState([]);
    const [usersOptions, setUsersOptions] = useState([]);
    // Flag to indicate whether a search is active.
    const [isSearchActive, setIsSearchActive] = useState(false);

    // Modal states.
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showEnquiryFormModal, setShowEnquiryFormModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyInquiryId, setHistoryInquiryId] = useState(null);

    // Responsive handling using theme and media query.
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    // -------------------- EFFECTS & RESPONSIVE HANDLING --------------------
    // Update window width state on resize.
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // -------------------- DATA FETCHING FUNCTIONS --------------------
    /**
     * fetchEnquiries
     * ----------------
     * Fetches enquiries from the backend based on the given category, offset, and filter values.
     * @param {string} category - The enquiry category (e.g., "Today").
     * @param {number} currentOffset - The offset for pagination.
     * @param {object|null} filtersOverride - Optional. If provided, these filters override the current search states.
     * @returns {Array} - Array of fetched enquiry records.
     */
    const fetchEnquiries = async (category, currentOffset = 0, filtersOverride = null) => {
        try {
            // Build filters object.
            let filtersToSend = {};
            if (filtersOverride === null) {
                if (searchInquiryDate.trim() !== "") {
                    filtersToSend.inquiryDate = searchInquiryDate.trim();
                }
                if (searchInquiryNumber.trim() !== "") {
                    filtersToSend.inquiryNumber = searchInquiryNumber.trim();
                }
                if (searchStatus.trim() !== "") {
                    filtersToSend.status = searchStatus.trim();
                }
                if (searchAssign.trim() !== "") {
                    filtersToSend.assign = searchAssign.trim();
                }
                if (searchCompanyName.trim() !== "") {
                    filtersToSend.companyName = searchCompanyName.trim();
                }
                if (searchContactPerson.trim() !== "") {
                    filtersToSend.contactPerson = searchContactPerson.trim();
                }
                if (searchMobileNo.trim() !== "") {
                    filtersToSend.mobileNo = searchMobileNo.trim();
                }
            } else {
                // Use the provided filters (for resetting, this will be an empty object).
                filtersToSend = filtersOverride;
            }

            // Build API query parameters.
            const params = { category, offset: currentOffset, limit: 10, userId: loggedInUser.userId, companyId: loggedInUser.companyId, role: loggedInUser.userCategory, ...filtersToSend, };

            const res = await axios.get(`${path}/api/get-enquiries`, { params });
            console.log("Fetched enquiries:", res.data);
            if (res.data.total !== undefined) {
                setTotalRecords(res.data.total);
                if (currentOffset === 0) {
                    setDisplayedRecords(res.data.enquiries.length);
                } else {
                    setDisplayedRecords(prev => prev + res.data.enquiries.length);
                }
            }
            return res.data.enquiries || [];
        } catch (error) {
            console.error("Error fetching enquiries:", error);
            return [];
        }
    };

    // -------------------- INITIAL DATA LOAD & COUNTS --------------------
    useEffect(() => {
        const fetchData = async () => {
            try {
                const newData = {};
                const newOffsets = {};
                for (const cat of categories) {
                    const records = await fetchEnquiries(cat, 0);
                    if (loggedInUser.userCategory === "Team Leader") {
                        const splitData = splitRecordsForTeamLeader(records, loggedInUser.userId);
                        newData[cat] = splitData;
                        newOffsets[cat] = { self: splitData.self.length, team: splitData.team.length };
                    } else {
                        newData[cat] = records;
                        newOffsets[cat] = records.length;
                    }
                }
                setDataSets(newData);
                setOffsets(newOffsets);

                const countRes = await axios.get(`${path}/api/get-counts`, {
                    params: { userId: loggedInUser.userId, role: loggedInUser.userCategory },
                });
                let counts = countRes.data;
                if (counts["Up Comming"] !== undefined) {
                    counts["Upcomming"] = counts["Up Comming"];
                    delete counts["Up Comming"];
                }
                setRecordCounts(counts);
            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };
        fetchData();
    }, [loggedInUser]);

    // -------------------- FETCH DROPDOWN OPTIONS WHEN SEARCH MODAL OPENS --------------------
    useEffect(() => {
        if (showSearchModal) {
            axios
                .get(`${path}/api/status`)
                .then((res) =>
                    setStatusOptions(Array.isArray(res.data.data) ? res.data.data : [])
                )
                .catch((error) => console.error("Error fetching status:", error));
            axios
                .get(`${path}/api/users`, {
                    params: { companyId: loggedInUser.companyId }
                })
                .then((res) =>
                    setUsersOptions(Array.isArray(res.data.data) ? res.data.data : [])
                )
                .catch((error) => console.error("Error fetching users:", error));
        }
    }, [showSearchModal]);

    // -------------------- FETCH THE LATEST INQUIRY NUMBER ON MOUNT --------------------
    useEffect(() => {
        const fetchInquiryNumber = async () => {
            try {
                const res = await axios.get(`${path}/api/getInqNumber`);
                setInquiryNumber(res.data.inquiryNumber);
            } catch (error) {
                console.error("Error fetching inquiry number:", error);
            }
        };
        fetchInquiryNumber();
    }, []);

    // -------------------- EVENT HANDLERS --------------------
    /**
     * toggleTable
     * -----------
     * Clears individual search states, deactivates search mode, and toggles the visible table.
     * Also closes the search modal.
     * @param {string} type - The selected enquiry category.
     */
    const toggleTable = (type) => {
        // Clear individual search filter states.
        setSearchInquiryDate("");
        setSearchInquiryNumber("");
        setSearchStatus("");
        setSearchAssign("");
        setSearchCompanyName("");
        setSearchContactPerson("");
        setSearchMobileNo("");
        setIsSearchActive(false);
        // Toggle the visible table.
        setVisibleTable((prev) => (prev === type ? null : type));
        // Close the search modal.
        setShowSearchModal(false);
    };

    // Individual search field handlers.
    const handleSearchInquiryDateChange = (e) => setSearchInquiryDate(e.target.value);
    const handleSearchInquiryNumberChange = (e) => setSearchInquiryNumber(e.target.value);
    const handleSearchCompanyNameChange = (e) => setSearchCompanyName(e.target.value);
    const handleSearchContactPersonChange = (e) => setSearchContactPerson(e.target.value);
    const handleSearchMobileNoChange = (e) => setSearchMobileNo(e.target.value);

    /**
     * handleModalSearch
     * -----------------
     * Triggered when the Search button in the search modal is clicked.
     * It fetches enquiries based on the current filter values and updates the displayed data.
     */
    const handleModalSearch = async () => {
        if (visibleTable) {
            const records = await fetchEnquiries(visibleTable, 0);
            if (loggedInUser.userCategory === "Team Leader") {
                const splitData = splitRecordsForTeamLeader(records, loggedInUser.userId);
                setDataSets((prev) => ({ ...prev, [visibleTable]: splitData }));
            } else {
                setDataSets((prev) => ({ ...prev, [visibleTable]: records }));
            }
            setIsSearchActive(true);
            setShowSearchModal(false);
        }
    };

    /**
     * handleCloseSearchModal (Reset Search)
     * ---------------------------------------
     * Clears all search filters and reloads the enquiries for the visible category exactly as initially loaded.
     * Uses an override of filters (an empty object) to ensure no filters are applied.
     */
    const handleCloseSearchModal = async () => {
        // Clear search filter states.
        setSearchInquiryDate("");
        setSearchInquiryNumber("");
        setSearchStatus("");
        setSearchAssign("");
        setSearchCompanyName("");
        setSearchContactPerson("");
        setSearchMobileNo("");
        setIsSearchActive(false);
        // Close the search modal.
        setShowSearchModal(false);
        // Reload enquiries without filters (offset 0).
        if (visibleTable) {
            const records = await fetchEnquiries(visibleTable, 0, {}); // Pass an empty filter override.
            if (loggedInUser.userCategory === "Team Leader") {
                const splitData = splitRecordsForTeamLeader(records, loggedInUser.userId);
                setDataSets((prev) => ({ ...prev, [visibleTable]: splitData }));
                // Optionally update offsets if needed.
                setOffsets((prev) => ({ ...prev, [visibleTable]: { self: splitData.self.length, team: splitData.team.length } }));
            } else {
                setDataSets((prev) => ({ ...prev, [visibleTable]: records }));
                setOffsets((prev) => ({ ...prev, [visibleTable]: records.length }));
            }
        }
    };

    /**
     * handleRowClick
     * --------------
     * Triggered when a record row is clicked.
     * Sets the selected record and opens the enquiry form modal.
     * @param {object} record - The clicked enquiry record.
     */
    const handleRowClick = (record) => {
        console.log(record);
        setSelectedRecord(record);
        setShowEnquiryFormModal(true);
    };

    /**
     * handleOpenHistory
     * -----------------
     * Opens the history modal for the specified enquiry.
     * @param {number|string} inquiryId - The ID of the enquiry.
     */
    const handleOpenHistory = (inquiryId) => {
        setHistoryInquiryId(inquiryId);
        setShowHistoryModal(true);
    };

    /**
     * handleShowMore
     * --------------
     * Fetches the next batch of records (pagination) for the currently visible category.
     */
    const handleShowMore = async () => {
        if (!visibleTable) return;
        try {
            const currentOffset = offsets[visibleTable] || 0;
            const newRecords = await fetchEnquiries(visibleTable, currentOffset);
            if (loggedInUser.userCategory === "Team Leader") {
                const splitNew = splitRecordsForTeamLeader(newRecords, loggedInUser.userId);
                setDataSets((prev) => ({
                    ...prev,
                    [visibleTable]: { self: [...(prev[visibleTable]?.self || []), ...splitNew.self], team: [...(prev[visibleTable]?.team || []), ...splitNew.team], },
                }));
            } else {
                if (newRecords.length > 0) {
                    setDataSets((prev) => ({ ...prev, [visibleTable]: [...prev[visibleTable], ...newRecords] }));
                }
            }
            setDisplayedRecords(prev => prev + newRecords.length);
        } catch (error) {
            console.error("Error fetching more records:", error);
        }
    };

    // -------------------- RENDER FUNCTIONS --------------------
    /**
     * HL_FN_RenderDesktopTable
     * --------------------------
     * Renders the enquiries in a desktop-friendly table view.
     * @param {Array} data - The array of enquiry records to render.
     */
    const HL_FN_RenderDesktopTable = (data) => (
        <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%", boxShadow: 3 }}>
            <Table>
                <TableHead sx={{ backgroundColor: "grey.300" }}>
                    <TableRow>
                        <TableCell align="center">#</TableCell>
                        <TableCell>Inq. No</TableCell>
                        <TableCell>Inq. Date</TableCell>
                        <TableCell>Follow-Up Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Client Name</TableCell>
                        <TableCell>Contact Person</TableCell>
                        <TableCell>Mobile No</TableCell>
                        <TableCell>Email Id</TableCell>
                        <TableCell>Assign</TableCell>
                        <TableCell align="center">History</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((record, index) => (
                        <TableRow key={record.inquiryId} hover onClick={() => handleRowClick(record)}
                            sx={{
                                backgroundColor: index % 2 === 0 ? "white" : "grey.50",
                                cursor: "pointer",
                            }}
                        >
                            <TableCell align="center">{index + 1}</TableCell>
                            <TableCell>{record.inquiryNumber}</TableCell>
                            <TableCell>
                                <FormattedDate dateString={record.inquiryDate} />
                            </TableCell>
                            <TableCell>
                                <FormattedDate dateString={record.nextFollowUpDate} />
                            </TableCell>
                            <TableCell>{record.statusName}</TableCell>
                            <TableCell>{record.priority}</TableCell>
                            <TableCell>{record.clientname}</TableCell>
                            <TableCell>{record.contactPerson}</TableCell>
                            <TableCell>{record.mobileNo}</TableCell>
                            <TableCell>{record.emailId}</TableCell>
                            <TableCell>{record.employeeName || "Unassigned"}</TableCell>
                            <TableCell align="center">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenHistory(record.inquiryId);
                                    }}
                                >
                                    <HistoryIcon sx={{ color: "primary.main" }} />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    /**
     * HL_FN_RenderMobileCard
     * ------------------------
     * Renders the enquiries in a mobile-friendly card view.
     * @param {Array} data - The array of enquiry records to render.
     */
    const HL_FN_RenderMobileCard = (data) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, px: 2 }}>
            {data.map((record, index) => (
                <Card key={record.inquiryId} variant="outlined" sx={{ cursor: "pointer", boxShadow: 2, position: "relative", p: 2, }} onClick={() => handleRowClick(record)} >
                    {/* History Button Positioned at Upper Right */}
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenHistory(record.inquiryId); }} sx={{ position: "absolute", top: 8, right: 8, backgroundColor: "white", "&:hover": { backgroundColor: "grey.100" }, zIndex: 1, }} >
                        <HistoryIcon sx={{ color: "primary.main" }} />
                    </IconButton>

                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            #{index + 1} - {record.inquiryNumber}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Inq. Date:</strong> <FormattedDate dateString={record.inquiryDate} />
                        </Typography>
                        <Typography variant="body2">
                            <strong>Follow-Up:</strong> <FormattedDate dateString={record.nextFollowUpDate} />
                        </Typography>
                        <Typography variant="body2">
                            <strong>Status:</strong> {record.statusName}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Priority:</strong> {record.priority}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Client Name:</strong> {record.clientname}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Contact Person:</strong> {record.contactPerson}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Mobile No:</strong> {record.mobileNo}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Email Id:</strong> {record.emailId}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Assign:</strong> {record.employeeName || "Unassigned"}
                        </Typography>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );

    /**
     * FnRenderTable
     * -------------
     * Determines whether to render the desktop table or mobile card view based on window width.
     * @param {object} param0 - Contains the data array to be rendered.
     * @returns The rendered table or card view.
     */
    const FnRenderTable = ({ data }) => {
        if (!data || data.length === 0) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 150, }} >
                    <Typography variant="h5" color="text.secondary">
                        No records found.
                    </Typography>
                </Box>
            );
        }
        return windowWidth < 1024 ? HL_FN_RenderMobileCard(data) : HL_FN_RenderDesktopTable(data);
    };

    // For team leaders, merge self and team enquiries into one array.
    let mergedInquiries = [];
    if (visibleTable && loggedInUser.userCategory === "Team Leader") {
        mergedInquiries = [...(dataSets[visibleTable]?.self || []), ...(dataSets[visibleTable]?.team || []),];
    }

    // -------------------- COMPONENT RENDER --------------------
    return (
        <Container sx={{ py: 3 }}>
            <Typography variant="h4" align="center" gutterBottom sx={{ textDecoration: "underline" }}>
                Inquiry Follow-Up
            </Typography>

            {/* MASTER CARDS SECTION */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",            // Mobile: Single column
                        sm: "repeat(2, 1fr)", // Small devices
                        md: "repeat(3, 1fr)", // Medium
                        lg: "repeat(5, 1fr)", // Large
                    },
                    gap: { xs: 1.5, sm: 3, md: 3 },
                    my: 3,
                }}
            >
                {categories.map((type, index) => {
                    const colors = ["#FACC15", "#4ADE80", "#60A5FA", "#A78BFA", "#F87171"];
                    const icons = {
                        Missed: <HiOutlineClock style={{ width: 28, height: 28, color: "white" }} />,
                        Today: <HiOutlineCalendar style={{ width: 28, height: 28, color: "white" }} />,
                        Upcoming: <FaCalendarAlt style={{ width: 28, height: 28, color: "white" }} />,
                        Delayed: <FaHourglassHalf style={{ width: 28, height: 28, color: "white" }} />,
                        Failed: <HiOutlineXCircle style={{ width: 28, height: 28, color: "white" }} />,
                    };

                    const categoryKey = type === "Upcomming" ? "Upcoming" : type;

                    return (
                        <Box
                            key={type}
                            onClick={() => toggleTable(type)}
                            sx={{
                                cursor: "pointer",
                                color: "white",
                                fontWeight: "bold",
                                boxShadow: { xs: 3, sm: 2 },
                                borderRadius: 4,
                                display: "flex",
                                flexDirection: {
                                    xs: "row",   // Horizontal for mobile
                                    sm: "column",
                                },
                                alignItems: "center",
                                justifyContent: {
                                    xs: "space-between", // Space between icon, text, and count
                                    sm: "center",
                                },
                                textAlign: {
                                    xs: "left",
                                    sm: "center",
                                },
                                width: "100%",
                                height: {
                                    xs: 55,      // Reduced height on mobile
                                    sm: 110,
                                    md: 130,
                                    lg: 140,
                                },
                                transition: "all 0.25s ease-in-out",
                                "&:hover": {
                                    transform: "scale(1.03)",
                                    boxShadow: 6,
                                },
                                backgroundColor: colors[index % colors.length],
                                p: { xs: 1, sm: 2.5, md: 3 },
                            }}
                        >
                            {/* Icon */}
                            <Box sx={{ mr: { xs: 1.5, sm: 0 }, ml: { xs: 1, sm: 0 } }}>
                                {icons[categoryKey]}
                            </Box>

                            {/* Category Title */}
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    flex: 1,
                                    fontSize: { xs: "0.9rem", sm: "0.9rem", md: "1rem", lg: "1.1rem" },
                                    textAlign: "center",
                                }}
                            >
                                {type.toUpperCase()}
                            </Typography>

                            {/* Enquiry Count */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: { xs: "0.8rem", sm: "0.8rem", md: "0.9rem", lg: "1rem" },
                                    mr: { xs: 1, sm: 0 },
                                    textAlign: "right",
                                }}
                            >
                                {recordCounts[type] !== undefined ? recordCounts[type] : 0}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* TOP SECTION: Search, Count & Show More */}
            {visibleTable && (
                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", justifyContent: "space-between", mb: 2, px: { xs: 2, md: 4 }, mt: 2, }}>
                    <Typography variant="subtitle1" sx={{ mb: { xs: 2, md: 0 } }}>
                        Count:{" "} {loggedInUser.userCategory === "Team Leader" ? `${(dataSets[visibleTable]?.self || []).length + (dataSets[visibleTable]?.team || []).length}` : dataSets[visibleTable]?.length || 0}{" "} / {recordCounts[visibleTable] || 0}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        {/* Open search modal to enter filter values */}
                        <Button variant="contained" color="success" onClick={() => setShowSearchModal(true)} startIcon={<HiOutlinePlusCircle />} sx={{ height: 40, borderRadius: 1 }}> Search </Button>
                        {/* Load more records if available */}
                        <Button variant="contained" color="primary" onClick={handleShowMore} disabled={displayedRecords >= totalRecords} sx={{ borderRadius: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                Show More <MdExpandMore size={20} style={{ marginLeft: 4 }} />
                            </Box>
                        </Button>
                    </Box>
                </Box>
            )}

            {/* SEARCH POPUP MODAL */}
            <Dialog open={showSearchModal} onClose={handleCloseSearchModal} fullWidth maxWidth="sm" BackdropProps={{ onClick: handleCloseSearchModal }} >
                <DialogTitle sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", }} >
                    Search Follow-Up
                    {/* Cross button to close the modal */}
                    <IconButton onClick={handleCloseSearchModal} sx={{ color: "grey.600" }}>
                        <HiOutlineXCircle />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <DateInput label="Inq. Date" name="inquiryDate" value={searchInquiryDate} onChange={handleSearchInquiryDateChange} fullWidth />
                        </Grid>
                        <Grid item xs={6}>
                            <FloatingLabelInput label="Inq. No" name="inquiryNumber" value={searchInquiryNumber} onChange={handleSearchInquiryNumberChange} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <FloatingLabelInput label="Client Name" name="companyName" value={searchCompanyName} onChange={handleSearchCompanyNameChange} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <FloatingLabelInput label="Contact Person" name="contactPerson" value={searchContactPerson} onChange={handleSearchContactPersonChange} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <FloatingLabelInput label="Mobile No" name="mobileNo" value={searchMobileNo} onChange={handleSearchMobileNoChange} fullWidth />
                        </Grid>
                        <Grid item xs={6}>
                            <Dropdown id="status-dropdown" label="Status" name="status" value={searchStatus} options={Array.isArray(statusOptions) ? statusOptions : []} valueKey="LeadStatus" labelKey="LeadStatus" onChange={(selectedOption) => setSearchStatus(selectedOption.LeadStatus)} fullWidth />
                        </Grid>
                        <Grid item xs={6}>
                            <Dropdown id="assign-dropdown" label="Assign" name="assign" value={searchAssign} options={Array.isArray(usersOptions) ? usersOptions : []} valueKey="employeeName" labelKey="employeeName" onChange={(selectedOption) => setSearchAssign(selectedOption.employeeName)} fullWidth />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    {/* Search button triggers filtering using the current search values */}
                    <Button variant="contained" color="primary" onClick={handleModalSearch}>
                        Search
                    </Button>
                    {/* Reset button clears filter values and reloads initial records */}
                    <Button variant="contained" color="secondary" onClick={handleCloseSearchModal}>
                        Reset
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Render Inquiries */}
            {visibleTable && (
                <Box sx={{ width: "100%", overflowX: "auto", mt: 2 }}>
                    {loggedInUser.userCategory === "Team Leader" ? (
                        <Box>
                            <Typography variant="h5" align="center" sx={{ mt: 2 }}>
                                FOLLOW-UP DETAILS
                            </Typography>
                            <Box>{FnRenderTable({ data: mergedInquiries })}</Box>
                        </Box>
                    ) : (
                        <Box>{FnRenderTable({ data: dataSets[visibleTable] })}</Box>
                    )}
                </Box>
            )}

            {/* EnquiryForm Modal for Follow-Up Mode */}
            <Dialog open={showEnquiryFormModal} onClose={() => setShowEnquiryFormModal(false)} fullWidth maxWidth="md">
                <DialogTitle>
                    <Button onClick={() => setShowEnquiryFormModal(false)} sx={{ position: "absolute", top: 8, right: 8, backgroundColor: "red", color: "white", minWidth: 0, width: 32, height: 32, borderRadius: "50%", "&:hover": { backgroundColor: "darkred" }, }}> &times; </Button>
                </DialogTitle>
                <DialogContent>
                    <EnquiryForm loggedInUser={loggedInUser} inquiryData={selectedRecord} onClose={() => setShowEnquiryFormModal(false)} />
                </DialogContent>
            </Dialog>

            {/* History Management Modal */}
            <HistoryManagement open={showHistoryModal} onClose={() => setShowHistoryModal(false)} inquiryId={historyInquiryId} maxWidth={isDesktop ? "xl" : "sm"} PaperProps={{ sx: { width: isDesktop ? "900px" : "100%" } }} />
        </Container>
    );
};

export default FollowUpPage_v1;